import { useEffect, useMemo, useState } from 'react';

const API = import.meta.env.VITE_API_URL || '/api';

type ReportTab = 'overview' | 'sales' | 'inventory' | 'customers' | 'movements' | 'categories' | 'promos' | 'suppliers' | 'labels';
type RangeKey = 'today' | 'week' | 'month' | 'currentMonth';

interface DashboardStats {
  today: { salesCount: number; totalSales: number };
  month: { salesCount: number; totalSales: number };
  inventory: { productCount: number; lowStockCount: number };
  billing: { invoiceCount: number; totalInvoiced: number; pendingInvoices: number };
}

interface SalesByDay {
  date: string;
  count: number;
  total: number;
}

interface TopProduct {
  productId: string;
  productName: string;
  totalQuantity: number;
  totalRevenue: number;
}

interface SalesPayment {
  paymentMethod: string;
  count: number;
  total: number;
  avgTicket: number;
}

interface ProfitMarginItem {
  productId: string;
  productName: string;
  salePrice: number;
  costPrice?: number;
  stockQty?: number;
  margin: number;
  profitPerUnit: number;
  potentialProfit: number;
}

interface LowStockItem {
  productId: string;
  productName: string;
  sku?: string;
  quantity: number;
  minQuantity: number;
  shortage: number;
  lastCost?: number;
  suggestedOrder: number;
}

interface InventoryValuation {
  totalProducts: number;
  totalUnits: number;
  costValue: number;
  saleValue: number;
  potentialProfit: number;
  productsWithoutStock: number;
}

interface CustomerRank {
  customerId: string;
  customerName?: string;
  purchaseCount: number;
  totalSpent: number;
  avgTicket: number;
  lastPurchase?: string;
}

// Cuenta corriente de clientes (cuentas por cobrar)
interface CreditSummaryItem {
  id: string;
  name: string;
  balance: number;
  creditLimit: number;
  creditEnabled: boolean;
  pendingDebits: number;
  nextPaymentDate: string | null;
}

interface StockMovement {
  id: string;
  productName?: string;
  type: 'in' | 'out' | 'adjustment' | string;
  quantity: number;
  reason?: string;
  refType?: string;
  createdAt: string;
}

interface CategoryPerf {
  categoryId: string | null;
  categoryName: string;
  revenue: number;
  margin: number;
  marginPct: number;
  units: number;
  stockUnits: number;
  sharePct: number;
  monthsOfStock: number | null;
}

interface PromoCandidate {
  productId: string;
  productName: string;
  stockQty: number;
  weeksOfSupply: number | null;
  soldLastDays: number;
  salePrice: number;
  costPrice: number;
  marginPct: number;
  suggestedDiscount: number;
  candidate: boolean;
}

interface SupplierPerf {
  supplierId: string;
  supplierName: string;
  productCount: number;
  revenue: number;
  margin: number;
  marginPct: number;
  units: number;
  stockUnits: number;
  stockValue: number;
  spentLastDays: number;
}

interface CoverageItem {
  productId: string;
  productName: string;
  stockQty: number;
  minQty: number;
  weeklySales: number;
  weeksOfSupply: number | null;
  stockValue: number;
  flag: string;
}

interface ReportState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

const TABS: { id: ReportTab; label: string; icon: string }[] = [
  { id: 'overview', label: 'Resumen', icon: '📊' },
  { id: 'sales', label: 'Ventas', icon: '💰' },
  { id: 'inventory', label: 'Inventario', icon: '📦' },
  { id: 'customers', label: 'Clientes', icon: '👥' },
  { id: 'movements', label: 'Movimientos', icon: '📋' },
  { id: 'categories', label: 'Categorías', icon: '📈' },
  { id: 'promos', label: 'Promociones', icon: '🎯' },
  { id: 'suppliers', label: 'Compras', icon: '🚚' },
  { id: 'labels', label: 'Etiquetas', icon: '🏷️' },
];

const RANGE_OPTIONS: { key: RangeKey; label: string }[] = [
  { key: 'today', label: 'Hoy' },
  { key: 'week', label: '7 días' },
  { key: 'month', label: '30 días' },
  { key: 'currentMonth', label: 'Mes' },
];

const moneyFmt = new Intl.NumberFormat('es-AR', {
  style: 'currency',
  currency: 'ARS',
});
const numberFmt = new Intl.NumberFormat('es-AR');

function formatMoney(value: number | string | undefined): string {
  const num = typeof value === 'string' ? parseFloat(value) : Number(value);
  if (!Number.isFinite(num)) return moneyFmt.format(0);
  return moneyFmt.format(num);
}

function formatNumber(value: number | undefined): string {
  return numberFmt.format(value ?? 0);
}

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString('es-AR', {
    day: 'numeric',
    month: 'short',
  });
}

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString('es-AR');
}

function getDaysForRange(range: RangeKey): number {
  if (range === 'today') return 1;
  if (range === 'week') return 7;
  if (range === 'month') return 30;
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const diff = Math.ceil(
    (now.getTime() - startOfMonth.getTime()) / (1000 * 60 * 60 * 24)
  );
  return Math.max(1, diff);
}

function paymentLabel(method: string): string {
  if (method === 'cash') return '💵 Efectivo';
  if (method === 'card') return '💳 Tarjeta';
  if (method === 'transfer') return '🏦 Transferencia';
  return method;
}

function movementLabel(type: string): { text: string; color: string } {
  if (type === 'in') return { text: '↑ Entrada', color: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' };
  if (type === 'out') return { text: '↓ Salida', color: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300' };
  return { text: '± Ajuste', color: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300' };
}

function emptyReportState<T>(): ReportState<T> {
  return { data: null, loading: false, error: null };
}

function KpiCard({
  label,
  value,
  subtitle,
  subtitleAccent,
  icon,
  colorClass,
  borderColor,
}: {
  label: string;
  value: string;
  subtitle: string;
  subtitleAccent?: boolean;
  icon: string;
  colorClass: string;
  borderColor: string;
}) {
  return (
    <div
      className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-5 border-l-4 min-h-[120px] flex flex-col justify-between"
      style={{ borderLeftColor: borderColor }}
    >
      <div className="flex items-start justify-between">
        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 font-medium uppercase tracking-wide">{label}</p>
        <span className={`text-xl sm:text-2xl ${colorClass}`}>{icon}</span>
      </div>
      <div>
        <p className={`text-2xl sm:text-3xl font-bold mt-2 ${colorClass}`}>{value}</p>
        <p className={`text-xs sm:text-sm mt-1 ${subtitleAccent ? 'text-red-600 font-semibold' : 'text-gray-500 dark:text-gray-400 dark:dark:text-gray-400'}`}>
          {subtitle}
        </p>
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-5 min-h-[120px]">
      <div className="flex items-start justify-between">
        <div className="h-3 w-24 bg-gray-200 rounded animate-pulse" />
        <div className="h-6 w-6 bg-gray-200 rounded animate-pulse" />
      </div>
      <div className="h-8 w-32 bg-gray-200 rounded mt-4 animate-pulse" />
      <div className="h-3 w-20 bg-gray-200 rounded mt-2 animate-pulse" />
    </div>
  );
}

function SkeletonList() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="h-20 bg-gray-200 rounded-lg animate-pulse" />
      ))}
    </div>
  );
}

function SkeletonBars() {
  return (
    <div className="h-56 flex items-end gap-2">
      {Array.from({ length: 7 }).map((_, i) => (
        <div
          key={i}
          className="flex-1 bg-gray-200 rounded-t animate-pulse"
          style={{ height: `${25 + (i % 4) * 15}%` }}
        />
      ))}
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="text-center py-8 text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">
      <p className="text-3xl mb-2">📭</p>
      <p className="font-medium">{message}</p>
    </div>
  );
}

function ErrorInline({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="bg-red-50 text-red-700 dark:text-red-300 p-4 rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <span className="text-sm font-medium">⚠️ {message}</span>
      <button
        onClick={onRetry}
        className="px-4 py-2 bg-red-700 text-white rounded-lg text-sm font-medium hover:bg-red-800 transition-colors min-h-[44px]"
      >
        Reintentar
      </button>
    </div>
  );
}

function SectionCard<T>({
  title,
  report,
  emptyMessage,
  onRetry,
  loadingContent,
  children,
}: {
  title: string;
  report: ReportState<T>;
  emptyMessage: string;
  onRetry: () => void;
  loadingContent?: React.ReactNode;
  children: React.ReactNode;
}) {
  const isEmpty =
    !report.loading &&
    !report.error &&
    (!report.data || (Array.isArray(report.data) && report.data.length === 0));

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-5">
      <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-3">{title}</h2>
      {report.loading && (loadingContent || <SkeletonList />)}
      {!report.loading && report.error && <ErrorInline message={report.error} onRetry={onRetry} />}
      {!report.loading && !report.error && isEmpty && <EmptyState message={emptyMessage} />}
      {!report.loading && !report.error && !isEmpty && children}
    </div>
  );
}

function DateRangeChips({ value, onChange }: { value: RangeKey; onChange: (r: RangeKey) => void }) {
  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label="Rango de fechas">
{RANGE_OPTIONS.map((opt) => {
        const selected = value === opt.key;
        return (
          <button
            key={opt.key}
            onClick={() => onChange(opt.key)}
            aria-pressed={selected}
            className={`px-4 py-2 rounded-full text-sm font-semibold min-h-[44px] transition-colors ${
              selected
                ? 'bg-blue-600 text-white shadow'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function PriceLabelsTab({ token }: { token: string | null }) {
  const headers = {
    'Authorization': `Bearer ${token || ''}`,
    'Content-Type': 'application/json',
    'x-tenant-slug': localStorage.getItem('tenantSlug') || '',
  };
  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);
  const [suppliers, setSuppliers] = useState<{ id: string; name: string }[]>([]);
  const [catalog, setCatalog] = useState<{ id: string; name: string; barcode?: string | null; sku?: string | null; salePrice: number | string }[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [categoryId, setCategoryId] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [search, setSearch] = useState('');
  const [format, setFormat] = useState('a4-70x37');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const FORMAT_OPTIONS = [
    { id: 'a4-70x37', label: '🏷️ A4 · 70×37 mm (etiqueta comercial)' },
    { id: 'a4-63x38', label: '🏷️ A4 · 63,5×38,1 mm (comercial)' },
    { id: 'a4-105x48', label: '🏷️ A4 · 105×48 mm (comercial)' },
    { id: 'a4-blank', label: '📄 A4 · hoja estándar (línea de recorte)' },
  ];

  useEffect(() => {
    loadFilters();
  }, []);

  async function loadFilters() {
    try {
      const [cRes, sRes] = await Promise.all([
        fetch(`${API}/categories`, { headers }),
        fetch(`${API}/suppliers`, { headers }),
      ]);
      if (cRes.ok) setCategories((await cRes.json()).data || []);
      if (sRes.ok) setSuppliers((await sRes.json()).data || []);
    } catch {
      // filtros opcionales
    }
  }

  async function loadCatalog(resetSelection = false) {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (categoryId && categoryId !== 'all') params.set('categoryId', categoryId);
      if (supplierId && supplierId !== 'all') params.set('supplierId', supplierId);
      if (search) params.set('q', search);
      const res = await fetch(`${API}/reports/price-label-catalog?${params}`, { headers });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Error al cargar productos');
      setCatalog(d.data || []);
      if (resetSelection) setSelected(new Set());
    } catch (e: any) {
      setError(e.message || 'Error inesperado');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadCatalog(true);
  }, [categoryId, supplierId]);

  function toggleProduct(id: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function filteredCatalog() {
    if (!search) return catalog;
    const q = search.toLowerCase();
    return catalog.filter(p => p.name.toLowerCase().includes(q));
  }

  async function generatePdf() {
    setError(null);
    const ids = selected.size > 0
      ? Array.from(selected).join(',')
      : catalog.map(p => p.id).join(',');
    if (!ids) {
      setError('Seleccioná al menos un producto');
      return;
    }
    try {
      const params = new URLSearchParams({
        productIds: ids,
        format,
      });
      if (categoryId && categoryId !== 'all') params.set('categoryId', categoryId);
      if (supplierId && supplierId !== 'all') params.set('supplierId', supplierId);
      const res = await fetch(`${API}/reports/price-labels.pdf?${params}`, { headers });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'Error al generar el PDF');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `etiquetas-precio-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e.message || 'Error al generar el PDF');
    }
  }

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-5">
        <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-3">🏷️ Etiquetas de precios</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">Categoría</label>
            <select
              value={categoryId}
              onChange={e => setCategoryId(e.target.value)}
              className="w-full px-3 py-2 border rounded min-h-[44px]"
            >
              <option value="">Todas</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Proveedor</label>
            <select
              value={supplierId}
              onChange={e => setSupplierId(e.target.value)}
              className="w-full px-3 py-2 border rounded min-h-[44px]"
            >
              <option value="">Todos</option>
              {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Formato de etiqueta</label>
            <select
              value={format}
              onChange={e => setFormat(e.target.value)}
              className="w-full px-3 py-2 border rounded min-h-[44px]"
            >
              {FORMAT_OPTIONS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
            </select>
          </div>
        </div>
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <input
            type="text"
            placeholder="🔍 Buscar producto..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full sm:w-64 px-3 py-2 border rounded min-h-[44px]"
          />
          <button
            onClick={() => loadCatalog(selected.size === 0)}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded font-medium min-h-[44px]"
          >
            Buscar
          </button>
          <button
            onClick={() => setSelected(new Set(catalog.map(p => p.id)))}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-800 border rounded text-sm font-medium min-h-[44px]"
          >
            Seleccionar todos
          </button>
          <button
            onClick={() => setSelected(new Set())}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-800 border rounded text-sm font-medium min-h-[44px]"
          >
            Limpiar
          </button>
        </div>

        {error && <p className="text-red-600 text-sm mb-3">{error}</p>}

        <div className="border rounded-lg overflow-y-auto max-h-80">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
              <tr className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
                <th className="py-2 px-3 w-10"></th>
                <th className="py-2 px-3">Producto</th>
                <th className="py-2 px-3 text-right">Precio venta</th>
                <th className="py-2 px-3 text-right hidden sm:table-cell">Código</th>
              </tr>
            </thead>
            <tbody>
              {filteredCatalog().map(p => (
                <tr key={p.id} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="py-2 px-3">
                    <input
                      type="checkbox"
                      checked={selected.has(p.id)}
                      onChange={() => toggleProduct(p.id)}
                      className="h-4 w-4"
                    />
                  </td>
                  <td className="py-2 px-3 font-medium">{p.name}</td>
                  <td className="py-2 px-3 text-right">{formatMoney(p.salePrice)}</td>
                  <td className="py-2 px-3 text-right hidden sm:table-cell text-gray-400">{p.barcode || p.sku || '—'}</td>
                </tr>
              ))}
              {filteredCatalog().length === 0 && (
                <tr>
                  <td colSpan={4} className="py-6 text-center text-gray-500">Sin productos</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {loading && <p className="text-xs text-gray-500 mt-2">Cargando...</p>}
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          {selected.size > 0 ? `📌 ${selected.size} producto(s) seleccionado(s)` : `Todos los ${catalog.length} de la lista`}
        </p>

        <div className="flex justify-end gap-3 mt-4">
          <button
            onClick={generatePdf}
            disabled={catalog.length === 0}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg font-medium min-h-[44px] transition-colors"
          >
            ⬇️ Generar PDF de etiquetas
          </button>
        </div>
      </div>
    </div>
  );
}

export function Reports() {
  const [activeTab, setActiveTab] = useState<ReportTab>('overview');
  const [range, setRange] = useState<RangeKey>('week');

  // Histórico de cuenta corriente (modal de detalle por cliente)
  const [histCustomer, setHistCustomer] = useState<{ id: string; name: string; balance: number; creditLimit: number; creditDays: number } | null>(null);
  const [histMovements, setHistMovements] = useState<{ id: string; type: string; amount: number; saleId: string | null; paymentDate: string | null; status: string; note: string | null; createdAt: string }[]>([]);
  const [histLoading, setHistLoading] = useState(false);

  const [reports, setReports] = useState<{
    dashboard: ReportState<DashboardStats>;
    salesByDay: ReportState<SalesByDay[]>;
    topProducts: ReportState<TopProduct[]>;
    salesByPayment: ReportState<SalesPayment[]>;
    profitMargin: ReportState<ProfitMarginItem[]>;
    lowStock: ReportState<LowStockItem[]>;
    inventoryValuation: ReportState<InventoryValuation>;
    customerRanking: ReportState<CustomerRank[]>;
    stockMovements: ReportState<StockMovement[]>;
    categoryPerformance: ReportState<CategoryPerf[]>;
    promoCandidates: ReportState<PromoCandidate[]>;
    supplierPerf: ReportState<SupplierPerf[]>;
    coverage: ReportState<CoverageItem[]>;
    creditSummary: ReportState<CreditSummaryItem[]>;
  }>({
    dashboard: emptyReportState(),
    salesByDay: emptyReportState(),
    topProducts: emptyReportState(),
    salesByPayment: emptyReportState(),
    profitMargin: emptyReportState(),
    lowStock: emptyReportState(),
    inventoryValuation: emptyReportState(),
    customerRanking: emptyReportState(),
    stockMovements: emptyReportState(),
    categoryPerformance: emptyReportState(),
    promoCandidates: emptyReportState(),
    supplierPerf: emptyReportState(),
    coverage: emptyReportState(),
    creditSummary: emptyReportState(),
  });

  const token = localStorage.getItem('accessToken');
  const headers = {
    Authorization: `Bearer ${token ?? ''}`,
    'Content-Type': 'application/json',
    'x-tenant-slug': localStorage.getItem('tenantSlug') || '',
  };

  async function apiGet<T>(path: string): Promise<T> {
    const res = await fetch(`${API}/reports/${path}`, { headers });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      throw new Error(body.error || `Error ${res.status}`);
    }
    const json = (await res.json()) as { data: T };
    return json.data;
  }

  function setReport<K extends keyof typeof reports>(
    key: K,
    patch: Partial<(typeof reports)[K]>
  ) {
    setReports((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  }

  async function loadDashboard() {
    setReport('dashboard', { loading: true, error: null });
    try {
      const data = await apiGet<DashboardStats>('dashboard');
      setReport('dashboard', { data, loading: false });
    } catch (err) {
      setReport('dashboard', {
        loading: false,
        error: err instanceof Error ? err.message : 'Error al cargar el resumen',
      });
    }
  }

  async function loadSalesByDay(days: number) {
    setReport('salesByDay', { loading: true, error: null });
    try {
      const data = await apiGet<SalesByDay[]>(`sales-by-day?days=${days}`);
      setReport('salesByDay', { data, loading: false });
    } catch (err) {
      setReport('salesByDay', {
        loading: false,
        error: err instanceof Error ? err.message : 'Error al cargar ventas por día',
      });
    }
  }

  async function loadTopProducts(days: number) {
    setReport('topProducts', { loading: true, error: null });
    try {
      const data = await apiGet<TopProduct[]>(`top-products?limit=5&days=${days}`);
      setReport('topProducts', { data, loading: false });
    } catch (err) {
      setReport('topProducts', {
        loading: false,
        error: err instanceof Error ? err.message : 'Error al cargar productos destacados',
      });
    }
  }

  function loadOverview(days: number) {
    loadDashboard();
    loadSalesByDay(days);
    loadTopProducts(days);
  }

  async function loadSalesTab(days: number) {
    setReport('salesByPayment', { loading: true, error: null });
    setReport('profitMargin', { loading: true, error: null });
    try {
      const [sp, pm] = await Promise.all([
        apiGet<SalesPayment[]>(`sales-by-payment?days=${days}`),
        apiGet<ProfitMarginItem[]>('profit-margin?limit=20'),
      ]);
      setReport('salesByPayment', { data: sp, loading: false });
      setReport('profitMargin', { data: pm, loading: false });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al cargar las ventas';
      setReport('salesByPayment', { loading: false, error: msg });
      setReport('profitMargin', { loading: false, error: msg });
    }
  }

  async function loadInventoryTab() {
    setReport('lowStock', { loading: true, error: null });
    setReport('inventoryValuation', { loading: true, error: null });
    try {
      const [ls, iv] = await Promise.all([
        apiGet<LowStockItem[]>('low-stock'),
        apiGet<InventoryValuation>('inventory-valuation'),
      ]);
      setReport('lowStock', { data: ls, loading: false });
      setReport('inventoryValuation', { data: iv, loading: false });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al cargar el inventario';
      setReport('lowStock', { loading: false, error: msg });
      setReport('inventoryValuation', { loading: false, error: msg });
    }
  }

  async function loadCustomerRanking(days: number) {
    setReport('customerRanking', { loading: true, error: null });
    try {
      const data = await apiGet<CustomerRank[]>(`customer-ranking?limit=10&days=${days}`);
      setReport('customerRanking', { data, loading: false });
    } catch (err) {
      setReport('customerRanking', {
        loading: false,
        error: err instanceof Error ? err.message : 'Error al cargar clientes',
      });
    }
  }

  // Cuentas por cobrar (cuenta corriente de clientes) — endpoint propio de customers
  async function loadCreditSummary() {
    setReport('creditSummary', { loading: true, error: null });
    try {
      const res = await fetch(`${API}/customers/credit-summary`, { headers });
      if (!res.ok) throw new Error(`Error ${res.status} al cargar cuentas por cobrar`);
      const json = (await res.json()) as { data: CreditSummaryItem[] };
      setReport('creditSummary', { data: json.data || [], loading: false });
    } catch (err) {
      setReport('creditSummary', {
        loading: false,
        error: err instanceof Error ? err.message : 'Error al cargar cuentas por cobrar',
      });
    }
  }

  // Histórico de cuenta corriente (GET /api/customers/:id/account)
  async function loadHistorico(c: { id: string; name: string }) {
    setHistCustomer({ ...c, balance: 0, creditLimit: 0, creditDays: 0 });
    setHistMovements([]);
    setHistLoading(true);
    try {
      const res = await fetch(`${API}/customers/${c.id}/account`, { headers });
      if (!res.ok) throw new Error(`Error ${res.status} al cargar el histórico`);
      const json = (await res.json()) as { data: {
        customer: { id: string; name: string; creditEnabled: boolean; creditLimit: string; creditDays: number };
        balance: number;
        movements: { id: string; type: string; amount: number; saleId: string | null; paymentDate: string | null; status: string; note: string | null; createdAt: string }[];
      } };
      setHistCustomer({
        id: json.data.customer.id,
        name: json.data.customer.name,
        balance: json.data.balance,
        creditLimit: Number(json.data.customer.creditLimit || 0),
        creditDays: json.data.customer.creditDays ?? 0,
      });
      setHistMovements(json.data.movements || []);
    } catch (err) {
      setHistCustomer(null);
      alert(err instanceof Error ? err.message : 'Error al cargar el histórico');
    } finally {
      setHistLoading(false);
    }
  }

  async function loadMovementsTab(days: number) {
    setReport('stockMovements', { loading: true, error: null });
    try {
      const data = await apiGet<StockMovement[]>(`stock-movements?days=${days}&limit=50`);
      setReport('stockMovements', { data, loading: false });
    } catch (err) {
      setReport('stockMovements', {
        loading: false,
        error: err instanceof Error ? err.message : 'Error al cargar movimientos',
      });
    }
  }

  async function loadCategoriesTab(days: number) {
    setReport('categoryPerformance', { loading: true, error: null });
    try {
      const data = await apiGet<CategoryPerf[]>(`category-performance?days=${days}`);
      setReport('categoryPerformance', { data, loading: false });
    } catch (err) {
      setReport('categoryPerformance', { loading: false, error: err instanceof Error ? err.message : 'Error al cargar categorías' });
    }
  }

  async function loadPromosTab(days: number) {
    setReport('promoCandidates', { loading: true, error: null });
    try {
      const data = await apiGet<PromoCandidate[]>(`promotion-candidates?days=${days}`);
      setReport('promoCandidates', { data, loading: false });
    } catch (err) {
      setReport('promoCandidates', { loading: false, error: err instanceof Error ? err.message : 'Error al cargar promociones' });
    }
  }

  async function loadSuppliersTab(days: number) {
    setReport('supplierPerf', { loading: true, error: null });
    try {
      const data = await apiGet<SupplierPerf[]>(`supplier-performance?days=${days}`);
      setReport('supplierPerf', { data, loading: false });
    } catch (err) {
      setReport('supplierPerf', { loading: false, error: err instanceof Error ? err.message : 'Error al cargar proveedores' });
    }
  }

  async function loadCoverage(days: number) {
    setReport('coverage', { loading: true, error: null });
    try {
      const data = await apiGet<CoverageItem[]>(`inventory-coverage?days=${days}`);
      setReport('coverage', { data, loading: false });
    } catch (err) {
      setReport('coverage', { loading: false, error: err instanceof Error ? err.message : 'Error al cargar cobertura' });
    }
  }

  function loadTab(tab: ReportTab, days: number) {
    switch (tab) {
      case 'sales':
        loadSalesTab(days);
        break;
      case 'inventory':
        loadInventoryTab();
        loadCoverage(getDaysForRange(range));
        break;
      case 'customers':
        loadCustomerRanking(days);
        loadCreditSummary();
        break;
      case 'movements':
        loadMovementsTab(days);
        break;
      case 'categories':
        loadCategoriesTab(days);
        break;
      case 'promos':
        loadPromosTab(days);
        break;
      case 'suppliers':
        loadSuppliersTab(days);
        break;
      default:
        break;
    }
  }

  useEffect(() => {
    const days = getDaysForRange(range);
    loadOverview(days);
  }, [range]);

  useEffect(() => {
    const days = getDaysForRange(range);
    loadTab(activeTab, days);
  }, [activeTab, range]);

  const maxSale = useMemo(() => {
    const data = reports.salesByDay.data ?? [];
    return Math.max(...data.map((d) => d.total), 1);
  }, [reports.salesByDay.data]);

  const dashboard = reports.dashboard.data;

  return (
    <>
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Reportes</h1>
          <p className="text-gray-600 dark:text-gray-300 mt-1 text-sm sm:text-base">Métricas y análisis del negocio</p>
        </div>
        <DateRangeChips value={range} onChange={setRange} />
      </div>

      {/* Tabs */}
      <div className="flex overflow-x-auto pb-2 gap-2 mb-6" role="tablist" aria-label="Reportes">
        {TABS.map((t) => {
          const selected = activeTab === t.id;
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={selected}
              onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap min-h-[44px] transition-colors ${
                selected
                  ? 'bg-blue-600 text-white shadow'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <span>{t.icon}</span>
              {t.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* KPIs */}
          <section>
            <h2 className="text-lg font-bold mb-3">Resumen</h2>
            {reports.dashboard.loading && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <SkeletonCard />
                <SkeletonCard />
                <SkeletonCard />
                <SkeletonCard />
              </div>
            )}
            {reports.dashboard.error && (
              <ErrorInline message={reports.dashboard.error} onRetry={loadDashboard} />
            )}
            {!reports.dashboard.loading && !reports.dashboard.error && dashboard && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <KpiCard
                  label="Ventas de Hoy"
                  value={formatMoney(dashboard.today.totalSales)}
                  subtitle={`${formatNumber(dashboard.today.salesCount)} ventas`}
                  icon="💰"
                  colorClass="text-blue-600 dark:text-blue-400"
                  borderColor="#2563eb"
                />
                <KpiCard
                  label="Ventas del Mes"
                  value={formatMoney(dashboard.month.totalSales)}
                  subtitle={`${formatNumber(dashboard.month.salesCount)} ventas`}
                  icon="📈"
                  colorClass="text-purple-600"
                  borderColor="#7c3aed"
                />
                <KpiCard
                  label="Productos Activos"
                  value={formatNumber(dashboard.inventory.productCount)}
                  subtitle={`${formatNumber(dashboard.inventory.lowStockCount)} bajo mínimo`}
                  subtitleAccent={dashboard.inventory.lowStockCount > 0}
                  icon="📦"
                  colorClass="text-green-600 dark:text-green-400"
                  borderColor="#059669"
                />
                <KpiCard
                  label="Facturado"
                  value={formatMoney(dashboard.billing.totalInvoiced)}
                  subtitle={`${formatNumber(dashboard.billing.pendingInvoices)} facturas pendientes`}
                  subtitleAccent={dashboard.billing.pendingInvoices > 0}
                  icon="🧾"
                  colorClass="text-amber-500"
                  borderColor="#f59e0b"
                />
              </div>
            )}
          </section>

          {/* Sales chart */}
          <SectionCard
            title="Ventas por día"
            report={reports.salesByDay}
            emptyMessage="No hay ventas en el período seleccionado."
            onRetry={() => loadSalesByDay(getDaysForRange(range))}
            loadingContent={<SkeletonBars />}
          >
            <div className="h-56 flex items-end gap-1 sm:gap-2">
              {reports.salesByDay.data?.map((day, idx) => {
                const height = maxSale > 0 ? (day.total / maxSale) * 100 : 0;
                return (
                  <div key={idx} className="flex-1 flex flex-col items-center justify-end min-w-0">
                    <span className="text-[10px] sm:text-xs mb-1 truncate w-full text-center">
                      {formatMoney(day.total)}
                    </span>
                    <div
                      className="w-full bg-blue-500 rounded-t"
                      style={{ height: `${Math.max(height, 1)}%`, minHeight: '4px' }}
                    />
                    <span className="text-[10px] sm:text-xs mt-1 text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 capitalize">
                      {new Date(day.date).toLocaleDateString('es-AR', { weekday: 'short' })}
                    </span>
                  </div>
                );
              })}
            </div>
          </SectionCard>

          {/* Top products */}
          <SectionCard
            title="Productos más vendidos"
            report={reports.topProducts}
            emptyMessage="No hay productos vendidos en el período."
            onRetry={() => loadTopProducts(getDaysForRange(range))}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {reports.topProducts.data?.map((p, idx) => (
                <div key={p.productId} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 sm:p-4">
                  <p className="font-semibold text-gray-800 dark:text-gray-100 text-sm sm:text-base mb-2">
                    #{idx + 1} {p.productName}
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">Cantidad</p>
                      <p className="font-bold">{formatNumber(p.totalQuantity)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">Ingresos</p>
                      <p className="font-bold text-green-600 dark:text-green-400">{formatMoney(p.totalRevenue)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>
        </div>
      )}

      {activeTab === 'sales' && (
        <div className="space-y-6">
          <SectionCard
            title="Ventas por medio de pago"
            report={reports.salesByPayment}
            emptyMessage="No hay ventas por método de pago."
            onRetry={() => loadSalesTab(getDaysForRange(range))}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {reports.salesByPayment.data?.map((s) => (
                <div key={s.paymentMethod} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 sm:p-4">
                  <p className="font-semibold text-gray-800 dark:text-gray-100 mb-3">{paymentLabel(s.paymentMethod)}</p>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">Ventas</p>
                      <p className="font-bold">{formatNumber(s.count)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">Total</p>
                      <p className="font-bold text-blue-600 dark:text-blue-400">{formatMoney(s.total)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">Ticket prom.</p>
                      <p className="font-bold">{formatMoney(s.avgTicket)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>

          <SectionCard
            title="Margen de ganancia por producto"
            report={reports.profitMargin}
            emptyMessage="No hay datos de margen."
            onRetry={() => loadSalesTab(getDaysForRange(range))}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {reports.profitMargin.data?.map((p) => (
                <div key={p.productId} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 sm:p-4">
                  <p className="font-semibold text-gray-800 dark:text-gray-100 text-sm sm:text-base mb-3">{p.productName}</p>
                  <div className="grid grid-cols-2 gap-y-2 gap-x-3 text-sm">
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">Precio</p>
                      <p>{formatMoney(p.salePrice)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">Costo</p>
                      <p>{formatMoney(p.costPrice)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">Margen</p>
                      <p>{formatNumber(p.margin)}%</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">Ganancia/u</p>
                      <p className="text-green-600 dark:text-green-400">{formatMoney(p.profitPerUnit)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">Stock</p>
                      <p>{formatNumber(p.stockQty)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">Ganancia pot.</p>
                      <p className="font-medium">{formatMoney(p.potentialProfit)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>
        </div>
      )}

      {activeTab === 'inventory' && (
        <div className="space-y-6">
          {/* Inventory valuation */}
          <section>
            <h2 className="text-lg font-bold mb-3">Valoración del inventario</h2>
            {reports.inventoryValuation.loading && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                <SkeletonCard />
                <SkeletonCard />
                <SkeletonCard />
                <SkeletonCard />
                <SkeletonCard />
              </div>
            )}
            {reports.inventoryValuation.error && (
              <ErrorInline message={reports.inventoryValuation.error} onRetry={loadInventoryTab} />
            )}
            {!reports.inventoryValuation.loading && !reports.inventoryValuation.error && reports.inventoryValuation.data && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                <KpiCard
                  label="Productos"
                  value={formatNumber(reports.inventoryValuation.data.totalProducts)}
                  subtitle={`${formatNumber(reports.inventoryValuation.data.productsWithoutStock)} sin stock`}
                  icon="📦"
                  colorClass="text-blue-600 dark:text-blue-400"
                  borderColor="#2563eb"
                />
                <KpiCard
                  label="Total Unidades"
                  value={formatNumber(reports.inventoryValuation.data.totalUnits)}
                  subtitle="En stock"
                  icon="📊"
                  colorClass="text-purple-600"
                  borderColor="#7c3aed"
                />
                <KpiCard
                  label="Valor Costo"
                  value={formatMoney(reports.inventoryValuation.data.costValue)}
                  subtitle="Inversión"
                  icon="💵"
                  colorClass="text-amber-500"
                  borderColor="#f59e0b"
                />
                <KpiCard
                  label="Valor Venta"
                  value={formatMoney(reports.inventoryValuation.data.saleValue)}
                  subtitle="Precio de venta"
                  icon="🏷️"
                  colorClass="text-green-600 dark:text-green-400"
                  borderColor="#059669"
                />
                <KpiCard
                  label="Ganancia Pot."
                  value={formatMoney(reports.inventoryValuation.data.potentialProfit)}
                  subtitle="Sobre stock actual"
                  icon="📈"
                  colorClass="text-pink-600"
                  borderColor="#db2777"
                />
              </div>
            )}
          </section>

          <SectionCard
            title="Productos bajo stock"
            report={reports.lowStock}
            emptyMessage="Todos los productos están sobre el mínimo."
            onRetry={loadInventoryTab}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {reports.lowStock.data?.map((p) => (
                <div
                  key={p.productId}
                  className="bg-red-50 rounded-lg p-3 sm:p-4 border border-red-100"
                >
                  <p className="font-semibold text-gray-800 dark:text-gray-100 text-sm sm:text-base">{p.productName}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 mb-3">{p.sku || 'Sin SKU'}</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">Stock actual</p>
                      <p className="font-bold text-red-600">{formatNumber(p.quantity)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">Mínimo</p>
                      <p className="font-bold">{formatNumber(p.minQuantity)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">Faltante</p>
                      <p className="font-bold text-red-600">{formatNumber(p.shortage)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">Sugerido</p>
                      <p className="font-bold text-blue-600 dark:text-blue-400">{formatNumber(p.suggestedOrder)} u.</p>
                    </div>
                  </div>
                  <div className="mt-2 text-sm">
                    <span className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">Último costo:</span>{' '}
                    {p.lastCost ? formatMoney(p.lastCost) : '-'}
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>
        </div>
      )}

      {activeTab === 'customers' && (
        <>
        <SectionCard
          title="Ranking de clientes"
          report={reports.customerRanking}
          emptyMessage="Sin ventas con clientes asignados todavía."
          onRetry={() => loadCustomerRanking(getDaysForRange(range))}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {reports.customerRanking.data?.map((c, idx) => (
              <div key={c.customerId} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 sm:p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="flex items-center justify-center w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-bold">
                    {idx + 1}
                  </span>
                  <p className="font-semibold text-gray-800 dark:text-gray-100 text-sm sm:text-base">{c.customerName || 'N/A'}</p>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">Compras</p>
                    <p className="font-bold">{formatNumber(c.purchaseCount)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">Total</p>
                    <p className="font-bold text-green-600 dark:text-green-400">{formatMoney(c.totalSpent)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">Ticket prom.</p>
                    <p>{formatMoney(c.avgTicket)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">Última compra</p>
                    <p>{c.lastPurchase ? formatDate(c.lastPurchase) : '-'}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>

        {/* Cuentas por cobrar (cuenta corriente) */}
        <SectionCard
          title="Cuentas por cobrar (cuenta corriente)"
          report={reports.creditSummary}
          emptyMessage="Ningún cliente tiene saldo pendiente en cuenta corriente."
          onRetry={() => loadCreditSummary()}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left">Cliente</th>
                  <th className="px-3 py-2 text-right">Saldo</th>
                  <th className="px-3 py-2 text-right">Límite</th>
                  <th className="px-3 py-2 text-left">Vencimientos</th>
                  <th className="px-3 py-2 text-left">Próx. pago</th>
                  <th className="px-3 py-2 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {reports.creditSummary.data?.map(c => (
                  <tr key={c.id} className="border-t">
                    <td className="px-3 py-2 font-medium">{c.name}</td>
                    <td className={`px-3 py-2 text-right font-bold ${c.balance > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                      {formatMoney(c.balance)}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-500 dark:text-gray-400">{formatMoney(c.creditLimit)}</td>
                    <td className="px-3 py-2 text-center">{c.pendingDebits}</td>
                    <td className="px-3 py-2">{c.nextPaymentDate ? formatDate(c.nextPaymentDate) : '—'}</td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => loadHistorico(c)}
                        className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium min-h-[44px]"
                      >
                        🕐 Histórico
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
        </>
      )}

      {activeTab === 'movements' && (
        <SectionCard
          title="Movimientos de stock"
          report={reports.stockMovements}
          emptyMessage="Sin movimientos en el período."
          onRetry={() => loadMovementsTab(getDaysForRange(range))}
        >
          <div className="grid grid-cols-1 gap-2">
            {reports.stockMovements.data?.map((m) => {
              const badge = movementLabel(m.type);
              return (
                <div
                  key={m.id}
                  className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-800 dark:text-gray-100 text-sm sm:text-base truncate">
                      {m.productName || 'N/A'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 truncate">
                      {formatDateTime(m.createdAt)} · {m.reason || 'Sin motivo'} · {m.refType || '-'}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${badge.color}`}>{badge.text}</span>
                    <span className="font-bold text-sm">{formatNumber(m.quantity)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </SectionCard>
      )}

      {activeTab === 'categories' && (
        <div className="space-y-6">
          <SectionCard
            title="Desempeño por categoría — mix, rentabilidad y cobertura"
            report={reports.categoryPerformance}
            emptyMessage="No hay ventas en el período."
            onRetry={() => loadCategoriesTab(getDaysForRange(range))}
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
                    <th className="py-2 pr-2">Categoría</th>
                    <th className="py-2 pr-2">Mix ventas</th>
                    <th className="py-2 pr-2 text-right">Ventas</th>
                    <th className="py-2 pr-2 text-right">Margen</th>
                    <th className="py-2 pr-2 text-right">Unid.</th>
                    <th className="py-2 pr-2 text-right">Stock</th>
                    <th className="py-2 text-right">Meses de stock</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.categoryPerformance.data?.map((c) => (
                    <tr key={c.categoryId || 'none'} className="border-t border-gray-100 dark:border-gray-700">
                      <td className="py-2 pr-2 font-medium">{c.categoryName}</td>
                      <td className="py-2 pr-2">
                        <div className="flex items-center gap-2">
                          <div className="w-20 bg-gray-100 dark:bg-gray-700 rounded-full h-2">
                            <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${Math.min(c.sharePct, 100)}%` }} />
                          </div>
                          <span className="text-xs text-gray-500">{c.sharePct.toFixed(1)}%</span>
                        </div>
                      </td>
                      <td className="py-2 pr-2 text-right font-semibold">{formatMoney(c.revenue)}</td>
                      <td className="py-2 pr-2 text-right">
                        <span className={c.marginPct >= 40 ? 'text-green-600 dark:text-green-400' : c.marginPct >= 25 ? 'text-amber-600' : 'text-red-600'}>
                          {c.marginPct.toFixed(1)}%
                        </span>
                      </td>
                      <td className="py-2 pr-2 text-right">{formatNumber(c.units)}</td>
                      <td className="py-2 pr-2 text-right">{formatNumber(c.stockUnits)}</td>
                      <td className="py-2 text-right">
                        {c.monthsOfStock == null ? (
                          <span className="text-gray-400">—</span>
                        ) : (
                          <span className={c.monthsOfStock > 6 ? 'text-red-600 font-semibold' : 'text-gray-700 dark:text-gray-300'}>
                            {c.monthsOfStock} m
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
              💡 Meses de stock alto + margen alto = categoría para empujar con promociones. Margen bajo + rotación alta = categoría a revisar precios con proveedores.
            </p>
          </SectionCard>
        </div>
      )}

      {activeTab === 'promos' && (
        <div className="space-y-6">
          <SectionCard
            title="🎯 Candidatos a promoción — stock parado con buen margen"
            report={reports.promoCandidates}
            emptyMessage="No hay candidatos en el período. 🎉"
            onRetry={() => loadPromosTab(getDaysForRange(range))}
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
                    <th className="py-2 pr-2">Producto</th>
                    <th className="py-2 pr-2 text-right">Stock</th>
                    <th className="py-2 pr-2 text-right">Cobertura</th>
                    <th className="py-2 pr-2 text-right">Vendidos*</th>
                    <th className="py-2 pr-2 text-right">Precio</th>
                    <th className="py-2 pr-2 text-right">Margen</th>
                    <th className="py-2 text-right">Descuento sugerido</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.promoCandidates.data?.map((p) => (
                    <tr key={p.productId} className="border-t border-gray-100 dark:border-gray-700">
                      <td className="py-2 pr-2 font-medium">{p.productName}</td>
                      <td className="py-2 pr-2 text-right">{formatNumber(p.stockQty)} u</td>
                      <td className="py-2 pr-2 text-right">
                        {p.weeksOfSupply == null ? '—' : (
                          <span className={p.weeksOfSupply >= 12 ? 'text-red-600 font-semibold' : 'text-amber-600 font-semibold'}>
                            {p.weeksOfSupply} sem
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-2 text-right text-gray-500">{formatNumber(p.soldLastDays)}</td>
                      <td className="py-2 pr-2 text-right">{formatMoney(p.salePrice)}</td>
                      <td className="py-2 pr-2 text-right text-green-600 dark:text-green-400">{p.marginPct.toFixed(1)}%</td>
                      <td className="py-2 text-right">
                        {p.suggestedDiscount > 0 ? (
                          <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 rounded-full text-xs font-bold">
                            −{p.suggestedDiscount}%
                          </span>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
              *Vendidos en el período. Cobertura = semanas de stock al ritmo de venta actual. El descuento sugerido iguala el margen al 15% — liberás capital sin vender a pérdida.
            </p>
          </SectionCard>
        </div>
      )}

      {activeTab === 'suppliers' && (
        <div className="space-y-6">
          <SectionCard
            title="🚚 Desempeño por proveedor — decisiones de compra"
            report={reports.supplierPerf}
            emptyMessage="No hay datos de proveedores."
            onRetry={() => loadSuppliersTab(getDaysForRange(range))}
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
                    <th className="py-2 pr-2">Proveedor</th>
                    <th className="py-2 pr-2 text-right">Productos</th>
                    <th className="py-2 pr-2 text-right">Ventas</th>
                    <th className="py-2 pr-2 text-right">Margen</th>
                    <th className="py-2 pr-2 text-right">Unid. vendidas</th>
                    <th className="py-2 pr-2 text-right">Unid. en stock</th>
                    <th className="py-2 text-right">Comprado (90d)</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.supplierPerf.data?.map((s) => (
                    <tr key={s.supplierId} className="border-t border-gray-100 dark:border-gray-700">
                      <td className="py-2 pr-2 font-medium">{s.supplierName}</td>
                      <td className="py-2 pr-2 text-right">{formatNumber(s.productCount)}</td>
                      <td className="py-2 pr-2 text-right font-semibold">{formatMoney(s.revenue)}</td>
                      <td className="py-2 pr-2 text-right">
                        <span className={s.marginPct >= 40 ? 'text-green-600 dark:text-green-400' : s.marginPct >= 25 ? 'text-amber-600' : 'text-red-600'}>
                          {s.marginPct.toFixed(1)}%
                        </span>
                      </td>
                      <td className="py-2 pr-2 text-right">{formatNumber(s.units)}</td>
                      <td className="py-2 pr-2 text-right text-gray-500">{formatNumber(s.stockUnits)}</td>
                      <td className="py-2 text-right">{formatMoney(s.spentLastDays)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
              💡 Stock alto de un proveedor + rotación baja = negociar devoluciones o próximas compras más chicas. Margen alto = renegociar volúmenes.
            </p>
          </SectionCard>
        </div>
      )}

      {/* ===== ETIQUETAS DE PRECIOS ===== */}
      {activeTab === 'labels' && (
        <PriceLabelsTab token={token} />
      )}

      {activeTab === 'inventory' && reports.coverage.data && (
        <SectionCard
          title="📦 Cobertura de stock — semanas por producto"
          report={reports.coverage}
          emptyMessage="No hay productos."
          onRetry={() => loadCoverage(getDaysForRange(range))}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
                  <th className="py-2 pr-2">Producto</th>
                  <th className="py-2 pr-2 text-right">Stock</th>
                  <th className="py-2 pr-2 text-right">Venta semanal</th>
                  <th className="py-2 pr-2 text-right">Cobertura</th>
                  <th className="py-2 pr-2 text-right">Valor</th>
                  <th className="py-2 text-right">Estado</th>
                </tr>
              </thead>
              <tbody>
                {reports.coverage.data.slice(0, 40).map((p) => {
                  const badge =
                    p.flag === 'out' ? { text: 'Sin stock', cls: 'bg-red-100 text-red-700' }
                    : p.flag === 'under' ? { text: 'Bajo stock', cls: 'bg-amber-100 text-amber-700' }
                    : p.flag === 'over' ? { text: 'Exceso', cls: 'bg-purple-100 text-purple-700' }
                    : p.flag === 'no_sales' ? { text: 'Sin ventas', cls: 'bg-gray-100 text-gray-600' }
                    : { text: 'OK', cls: 'bg-green-100 text-green-700' };
                  return (
                    <tr key={p.productId} className="border-t border-gray-100 dark:border-gray-700">
                      <td className="py-2 pr-2 font-medium">{p.productName}</td>
                      <td className="py-2 pr-2 text-right">{formatNumber(p.stockQty)}</td>
                      <td className="py-2 pr-2 text-right">{p.weeklySales.toFixed(1)}</td>
                      <td className="py-2 pr-2 text-right">
                        {p.weeksOfSupply == null ? '—' : `${p.weeksOfSupply} sem`}
                      </td>
                      <td className="py-2 pr-2 text-right">{formatMoney(p.stockValue)}</td>
                      <td className="py-2 text-right">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${badge.cls}`}>{badge.text}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </SectionCard>
      )}
    </div>

      {/* Modal Histórico de cuenta corriente */}
      {histCustomer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setHistCustomer(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h3 className="font-bold text-lg">Histórico — {histCustomer.name}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Límite: {formatMoney(histCustomer.creditLimit)} · Plazo: {histCustomer.creditDays} días ·
                  Saldo actual: {' '}
                  <span className={`font-bold ${histCustomer.balance > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                    {formatMoney(histCustomer.balance)}
                  </span>
                </p>
              </div>
              <button onClick={() => setHistCustomer(null)} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 px-2 py-1 text-xl leading-none">✕</button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {histLoading ? (
                <p className="text-sm text-gray-500">Cargando movimientos...</p>
              ) : histMovements.length === 0 ? (
                <p className="text-sm text-gray-500">Sin movimientos registrados.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-3 py-2 text-left">Fecha</th>
                      <th className="px-3 py-2 text-left">Tipo</th>
                      <th className="px-3 py-2 text-right">Monto</th>
                      <th className="px-3 py-2 text-left">Venc. / pago</th>
                      <th className="px-3 py-2 text-left">Estado</th>
                      <th className="px-3 py-2 text-left">Nota</th>
                    </tr>
                  </thead>
                  <tbody>
                    {histMovements.map(m => (
                      <tr key={m.id} className="border-t">
                        <td className="px-3 py-2">{formatDate(m.createdAt)}</td>
                        <td className="px-3 py-2">
                          {m.type === 'debit' ? (
                            <span className="text-red-600 dark:text-red-400">Débito (venta)</span>
                          ) : (
                            <span className="text-green-600 dark:text-green-400">Crédito (pago)</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold">{formatMoney(m.amount)}</td>
                        <td className="px-3 py-2">{m.paymentDate ? formatDate(m.paymentDate) : '—'}</td>
                        <td className="px-3 py-2">
                          {m.status === 'paid'
                            ? <span className="px-2 py-0.5 rounded text-xs bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300">Pagado</span>
                            : <span className="px-2 py-0.5 rounded text-xs bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300">Pendiente</span>}
                        </td>
                        <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{m.note || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
